import { plainToInstance } from "class-transformer"
import * as PapaParse from "papaparse"

import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { INTEGRATIONS_CONFIG } from "../config/integrations.config"
import { RawEraUploadCsvRowDTO } from "../dto/rawEraUploadCsvRow.dto"
import type {
    IEraUploadCsvPreviewResult,
    IEraUploadCsvValidationResult,
    IEraUploadRowDisplayColumnMapping,
    IEraUploadX12SegmentPresence,
    IEraUploadX12ValidationResult,
} from "../types/integrations.types"
import { CsvHelper } from "./csv.helper"
import { UtilHelper } from "./util.helper"

export class EraUploadHelper {
    /**
     * Read a review-table cell value from row data using the column mapping path.
     */
    static getRowDisplayValue(
        mapping: IEraUploadRowDisplayColumnMapping,
        row: RawEraUploadCsvRowDTO | null,
    ): string {
        const value = CsvHelper.getValueByPath(row, mapping.valuePath, "")

        if (value === "" || value === undefined || value === null) {
            return ""
        }

        return String(value)
    }

    /**
     * Infer CSV vs X12 from extension and, for .txt, a short content sniff (ISA vs CSV headers).
     */
    static async detectFileFormat(
        file: File,
        options?: { isCsvUploadEnabled?: boolean },
    ): Promise<OneVerifyEraUploadFileFormat | null> {
        const isCsvUploadEnabled = options?.isCsvUploadEnabled ?? true

        const extension = this.getFileExtension(file.name)

        if (
            isCsvUploadEnabled &&
            extension ===
                COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.allowedCsvExtension
        ) {
            return OneVerifyEraUploadFileFormat.csv
        }

        const isAllowedX12Extension =
            !!extension &&
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.isAllowedX12Extension(extension)

        if (!isAllowedX12Extension) {
            return null
        }

        if (
            COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.isX12PlainTextExtension(
                extension,
            )
        ) {
            return this.detectTxtFileFormat(file, isCsvUploadEnabled)
        }

        return OneVerifyEraUploadFileFormat.x12
    }

    /**
     * Format a byte count for display (B, KB, or MB).
     */
    static formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`
        }

        if (bytes < 1024 * 1024) {
            return `${(bytes / 1024).toFixed(1)} KB`
        }

        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    }

    /**
     * Confirm the file is an allowed X12 type and contains required ISA/GS/ST segments.
     */
    static async validateX12File(file: File) {
        const format = await this.detectFileFormat(file)

        if (format !== OneVerifyEraUploadFileFormat.x12) {
            return {
                isValid: false as const,
                errorMessage: `Unsupported file type. Use ${COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()}, or an X12 .txt file.`,
            }
        }

        const validation = await this.validateX12Structure(file)

        if (!validation.isValid) {
            return {
                isValid: false as const,
                errorMessage:
                    validation.errorMessage ??
                    "This file could not be validated as X12 835.",
            }
        }

        return {
            isValid: true as const,
        }
    }

    /**
     * Validate the file and return a capped preview of parsed rows plus total row count.
     */
    static async parseCsvFilePreview(
        file: File,
    ): Promise<IEraUploadCsvPreviewResult> {
        const validation = await this.validateCsvFile(file)

        if (!validation.isValid) {
            return {
                rows: [],
                totalRowCount: 0,
                validation,
            }
        }

        const rows = await this.parseCsvPreviewFromFile(
            file,
            INTEGRATIONS_CONFIG.eraUpload.maxPreviewTableRows,
        )

        return {
            rows,
            totalRowCount: validation.rowCount,
            validation,
        }
    }

    /**
     * Validate extension, required/known headers, and that at least one data row exists.
     */
    private static async validateCsvFile(
        file: File,
    ): Promise<IEraUploadCsvValidationResult> {
        const name = file.name.toLowerCase()

        if (
            !name.endsWith(".csv") &&
            file.type !== "text/csv" &&
            file.type !== "application/csv"
        ) {
            return {
                isValid: false,
                headers: [],
                rowCount: 0,
                errorMessage:
                    "Only .csv files are supported for spreadsheet upload.",
            }
        }

        const fileHeaders = await this.readCsvFileHeaders(file)

        if (!fileHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: "Could not read column headers from the file.",
            }
        }

        const missingHeaders = this.getMissingRequiredCsvHeaders(fileHeaders)

        if (missingHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: `Missing required column(s): ${missingHeaders.join(", ")}. Download the template and try again.`,
            }
        }

        const unknownHeaders = fileHeaders.filter(header => {
            const trimmed = header.trim()

            if (!trimmed) {
                return false
            }

            return !this.csvHeaderToDtoKey(trimmed)
        })

        if (unknownHeaders.length) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: `Unrecognized column(s): ${unknownHeaders.join(", ")}.`,
            }
        }

        const rowCount = await this.countCsvDataRows(file)

        if (rowCount < 1) {
            return {
                isValid: false,
                headers: fileHeaders,
                rowCount: 0,
                errorMessage: "The file does not contain any data rows.",
            }
        }

        return {
            isValid: true,
            headers: fileHeaders,
            rowCount,
        }
    }

    /**
     * For .txt files, peek at the start to distinguish X12 835 from CSV content.
     */
    private static async detectTxtFileFormat(
        file: File,
        isCsvUploadEnabled: boolean,
    ): Promise<OneVerifyEraUploadFileFormat | null> {
        const peek = await file.slice(0, 256).text()

        const trimmed = peek.replace(/^\uFEFF/, "").trimStart()

        const { isa } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        if (trimmed.startsWith(isa)) {
            return OneVerifyEraUploadFileFormat.x12
        }

        if (!isCsvUploadEnabled) {
            return null
        }

        const csvValidation = await this.validateCsvFile(file)

        if (csvValidation.isValid) {
            return OneVerifyEraUploadFileFormat.csv
        }

        return null
    }

    /**
     * Stream-parse a CSV file and collect up to maxRows review DTOs.
     */
    private static parseCsvPreviewFromFile(
        file: File,
        maxRows: number,
    ): Promise<RawEraUploadCsvRowDTO[]> {
        return new Promise((resolve, reject) => {
            const rows: RawEraUploadCsvRowDTO[] = []

            PapaParse.parse<Record<string, string>>(file, {
                header: true,
                skipEmptyLines: true,
                step: (results, parser) => {
                    const dto = this.mapParsedCsvRowToDto(
                        results.data,
                        rows.length,
                    )

                    if (!dto) {
                        return
                    }

                    rows.push(dto)

                    if (rows.length >= maxRows) {
                        parser.abort()
                    }
                },
                complete: () => {
                    resolve(rows)
                },
                error: error => {
                    reject(error)
                },
            })
        })
    }

    /**
     * Map a PapaParse row object to a review DTO, or null when the row should be skipped.
     */
    private static mapParsedCsvRowToDto(
        row: Record<string, string>,
        index: number,
    ): RawEraUploadCsvRowDTO | null {
        if (this.shouldSkipParsedCsvRow(row)) {
            return null
        }

        const raw: Record<string, string> = {}

        for (const [header, value] of Object.entries(row)) {
            const trimmedHeader = header.trim()

            const key = this.csvHeaderToDtoKey(trimmedHeader)

            if (key === undefined || value === undefined) {
                continue
            }

            raw[key] = String(value).trim()
        }

        if (this.isEmptyCsvRow(raw)) {
            return null
        }

        const dto = plainToInstance(RawEraUploadCsvRowDTO, raw)

        dto.index = index

        return dto
    }

    /**
     * Skip blank, comment, and comma-only rows before mapping to DTO fields.
     */
    private static shouldSkipParsedCsvRow(
        row: Record<string, string>,
    ): boolean {
        const values = Object.values(row).map(value => String(value ?? "").trim())

        const nonEmptyValues = values.filter(Boolean)

        if (nonEmptyValues.length === 0) {
            return true
        }

        if (
            nonEmptyValues.length === 1 &&
            nonEmptyValues[0].startsWith("#")
        ) {
            return true
        }

        if (values.every(value => /^[\s,]*$/.test(value))) {
            return true
        }

        const withoutQuotedEmpties = values
            .map(value => value.replace(/"\s*"/g, ""))
            .join(",")

        return /^[\s,]*$/.test(withoutQuotedEmpties)
    }

    /**
     * Lowercase extension from a file name, or undefined when absent.
     */
    private static getFileExtension(fileName: string): string | undefined {
        if (!fileName.includes(".")) {
            return undefined
        }

        return fileName.split(".").pop()?.toLowerCase()
    }

    /**
     * Parse the first line of CSV text into trimmed header names.
     */
    private static readCsvHeadersFromText(previewText: string): string[] {
        const firstLine = previewText.split(/\r?\n/)[0] ?? ""

        const parsed = PapaParse.parse<string[]>(firstLine, {
            header: false,
            skipEmptyLines: true,
        })

        return (parsed.data[0] ?? []).map(header => header.trim())
    }

    /**
     * Read column headers from the start of a file without loading the full contents.
     */
    private static async readCsvFileHeaders(file: File): Promise<string[]> {
        const previewBlob = file.slice(
            0,
            INTEGRATIONS_CONFIG.eraUpload.maxHeaderPreviewBytes,
        )

        const previewText = await previewBlob.text()

        return this.readCsvHeadersFromText(previewText)
    }

    /**
     * Template headers that are missing from the uploaded file.
     */
    private static getMissingRequiredCsvHeaders(
        fileHeaders: string[],
    ): string[] {
        const headerSet = new Set(fileHeaders.map(header => header.trim()))

        return Object.keys(
            INTEGRATIONS_CONFIG.eraUpload.ruleDisplayTableColumnMapping,
        )
            .map(key => UtilHelper.camelCaseToSnakeCase(key))
            .filter(requiredHeader => !headerSet.has(requiredHeader))
    }

    /**
     * Map a CSV header to a DTO key when it matches a known ERA upload column.
     */
    private static csvHeaderToDtoKey(
        header: string,
    ): keyof RawEraUploadCsvRowDTO | undefined {
        const key = UtilHelper.snakeCaseToCamelCase(header.trim())

        if (this.isKnownEraUploadCsvDtoKey(key)) {
            return key
        }

        return undefined
    }

    /**
     * Whether a camelCase key corresponds to an ERA upload CSV column.
     */
    private static isKnownEraUploadCsvDtoKey(
        key: string,
    ): key is keyof RawEraUploadCsvRowDTO {
        return Object.hasOwn(
            INTEGRATIONS_CONFIG.eraUpload.ruleDisplayTableColumnMapping,
            key,
        )
    }

    /**
     * Count data rows by streaming newlines (header row excluded). Used for large files.
     */
    private static async countCsvDataRows(file: File): Promise<number> {
        const reader = file.stream().getReader()

        const decoder = new TextDecoder()

        let newlineCount = 0

        while (true) {
            const { done, value } = await reader.read()

            if (done) {
                break
            }

            const chunk = decoder.decode(value, { stream: true })

            const matches = chunk.match(/\n/g)

            if (matches) {
                newlineCount += matches.length
            }
        }

        if (newlineCount === 0) {
            return 0
        }

        const dataRows = newlineCount - 1

        const endsWithNewline = await this.fileEndsWithNewline(file)

        return endsWithNewline ? dataRows : dataRows + 1
    }

    /**
     * Whether the file ends with a newline, for accurate row counting on the last line.
     */
    private static async fileEndsWithNewline(file: File): Promise<boolean> {
        if (file.size === 0) {
            return false
        }

        const tailBlob = file.slice(Math.max(0, file.size - 2), file.size)

        const tailText = await tailBlob.text()

        return tailText.endsWith("\n") || tailText.endsWith("\r")
    }

    /**
     * True when every cell in a parsed row is empty or whitespace.
     */
    private static isEmptyCsvRow(row: Record<string, unknown>): boolean {
        return Object.values(row).every(v => /^[\s,]*$/.test(String(v ?? "")))
    }

    /**
     * Validate that the file start contains ISA, GS, and ST segments (835 structure check).
     */
    private static async validateX12Structure(
        file: File,
    ): Promise<IEraUploadX12ValidationResult> {
        const previewBlob = file.slice(
            0,
            Math.min(
                file.size,
                INTEGRATIONS_CONFIG.eraUpload.maxX12StructurePreviewBytes,
            ),
        )
        const previewText = await previewBlob.text()

        const segments = this.getRequiredX12SegmentPresence(previewText)

        const { isa, gs, st } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        const missingSegments = [
            !segments.isa && isa,
            !segments.gs && gs,
            !segments.st && st,
        ].filter(Boolean) as string[]

        if (missingSegments.length) {
            return {
                isValid: false,
                segments,
                errorMessage: `Missing required X12 segment(s): ${missingSegments.join(", ")}. Confirm this is a valid 835 remittance file.`,
            }
        }

        return {
            isValid: true,
            segments,
        }
    }

    /**
     * Whether ISA, GS, and ST segment IDs appear in the parsed preview text.
     */
    private static getRequiredX12SegmentPresence(
        text: string,
    ): IEraUploadX12SegmentPresence {
        const segmentIds = this.getX12SegmentIds(text)

        const { isa, gs, st } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        return {
            isa: segmentIds.includes(isa),
            gs: segmentIds.includes(gs),
            st: segmentIds.includes(st),
        }
    }

    /**
     * Extract X12 segment IDs from a preview using ISA element and segment terminators.
     */
    private static getX12SegmentIds(text: string): string[] {
        const cleanText = text.replace(/^\uFEFF/, "").trimStart()

        const { isa } = INTEGRATIONS_CONFIG.eraUpload.x12RequiredSegmentIds

        if (!cleanText.startsWith(isa) || cleanText.length < 106) {
            return []
        }

        const elementSeparator = cleanText.charAt(3)

        const segmentTerminator = cleanText.charAt(105)

        return cleanText
            .split(segmentTerminator)
            .map(segment => segment.replace(/[\r\n]/g, "").trim())
            .filter(Boolean)
            .map(segment => segment.split(elementSeparator)[0]?.trim() ?? "")
            .filter(Boolean)
    }
}
